import { type IFormEngine } from '@form-engine/core-application/index';
import { type FormSession } from '@form-engine/core-application/types/form';
import { type JSONValue } from '@form-engine/server-application/typings';
import fetch from 'cross-fetch';

declare module '@form-engine/core-application/index' {
    interface IFormEngine {
        __currentError?: Error;
    }
}

export class FetchBackendError extends Error {
    constructor(
        public message: string,
        public url?: string,
        public status: number = 500,
        public errorCode: string = 'UNKNOWN_ERROR',
        public payload: JSONValue = null,
        public traceId?: string
    ) {
        super(message);
    }

    toJSON() {
        return {
            message: this.message,
            url: this.url,
            status: this.status,
            errorCode: this.errorCode,
            payload: this.payload,
            traceId: this.traceId,
        };
    }
}

export default async function fetchBackend(
    path: string,
    session: Partial<FormSession>,
    data?: JSONValue,
    options: RequestInit = {}
) {
    window.FormEngine ??= {} as IFormEngine;
    const response = await fetch(path, {
        method: 'POST',
        ...options,
        body: JSON.stringify({ session, data }),
        headers: {
            'content-type': 'application/json',
            accept: 'application/json',
            ...(options.headers || {}),
        },
    });

    if (response.status < 200 || response.status >= 400) {
        const message = await response.text();
        let error;
        if (message.startsWith('{')) {
            try {
                const errorInfo = JSON.parse(message);
                let errorData: Partial<FetchBackendError> | null = null;
                if (String(errorInfo?.error?.message).startsWith('{')) {
                    errorData = JSON.parse(errorInfo.error.message);
                }
                error = new FetchBackendError(
                    errorData?.message ?? message,
                    errorData?.url,
                    errorData?.status,
                    errorData?.errorCode,
                    errorData?.payload,
                    errorData?.traceId
                );
                window.FormEngine.__currentError = error;
            } catch {
                error = new FetchBackendError(message);
                window.FormEngine.__currentError = error;
            }
        } else {
            window.FormEngine.__currentError = new FetchBackendError(message);
        }

        throw error;
    }

    return await response.json();
}
